import { ApolloProvider } from '@apollo/client';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Box, FlexLayout, ThemeProvider } from '@deity/falcon-ui';
import { ThemeProvider as PortalUIProvider } from '@deity-io/portal-ui';
import { AuthTokenProvider } from 'auth/AuthTokenProvider';
import Loading from 'auth/Loading';
import { ApolloClientInstanceProvider, Footer, Header } from 'components';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import { WithConfigLoader } from 'components/WithConfigLoader/WithConfigLoader';
import { AppContextProvider } from 'contexts/AppContextProvider';
import { ContentContainer } from 'layouts';
import PackageVersions from 'pages/packages/package/PackageVersions';
import RuntimeStatus from 'pages/runtimeStatus/RuntimeStatus';
import type { FC } from 'react';
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import { dataIntegratorTheme, globalCss } from 'styling/theme';
import PackageRouter from './layouts/PackageRouter';
import Packages from './pages/packages/Packages';

const PackagesWrapper: FC = () => (
  <ContentContainer>
    <Box width="full" style={{ marginTop: '48px', paddingBottom: '100px' }}>
      <Packages />
    </Box>
  </ContentContainer>
);

const PackageVersionsWrapper: FC = () => (
  <ContentContainer>
    <Box width="full" style={{ marginTop: '48px', paddingBottom: '100px' }}>
      <PackageVersions />
    </Box>
  </ContentContainer>
);

const RuntimeStatusWrapper = () => (
  <ContentContainer>
    <Box width="full" style={{ marginTop: '48px', paddingBottom: '100px' }}>
      <RuntimeStatus />
    </Box>
  </ContentContainer>
);

const AppRouter: FC = () => (
  <Router>
    <Header />
    <ErrorBoundary>
      <Switch>
        <Route exact path="/packages" component={PackagesWrapper} />
        <Route path="/status" component={RuntimeStatusWrapper} />
        <Route path="/packages/:packageId/versions" component={PackageVersionsWrapper} />
        <Route path="/packages/:packageId/:versionUuid" component={PackageRouter} />
        <Redirect to="/packages" from="*" />
      </Switch>
    </ErrorBoundary>
    <Footer />
  </Router>
);

const App: FC = () => (
  <ThemeProvider theme={dataIntegratorTheme} globalCss={globalCss}>
    <PortalUIProvider>
      <AuthTokenProvider>
        {({ authToken }) => (
          <ApolloClientInstanceProvider authToken={authToken}>
            {({ client }) => (
              <ApolloProvider client={client}>
                <ErrorBoundary>
                  <AppContextProvider>
                    <WithConfigLoader>
                      <FlexLayout width="full" justifyContent="center">
                        <AppRouter />
                      </FlexLayout>
                    </WithConfigLoader>
                  </AppContextProvider>
                </ErrorBoundary>
              </ApolloProvider>
            )}
          </ApolloClientInstanceProvider>
        )}
      </AuthTokenProvider>
    </PortalUIProvider>
  </ThemeProvider>
);

export default withAuthenticationRequired(App, { onRedirecting: () => <Loading /> });
